/**
 * @method getContentBetween
 * @param {string} str the content string
 * @param {string|number} start the index or substring, which defines the start position
 * @param {string|number} end the index or substring, which defines the end position
 * @returns {string} returns the substring between the start position and end position
 */
export function getContentBetween( str, start, end ) {
  const startType = typeof start;
  const endType = typeof end;
  if( startType !== 'number' && startType !== 'string' || endType !== 'number' && endType !== 'string' ) {
    console.warn( `Expected parameters: 'start' and 'end' to be numbers or string, but got ${ startType } and ${ endType } ` );
    return str;
  }

  const startIndex = startType === 'number' ? start : str.indexOf( start );
  const endIndex = endType === 'number' ? end : str.indexOf( end, startIndex );

  return str?.substring( startIndex + 1, endIndex );
}

/**
 * @method countSubstringOccurrences
 * @param {string} str the content string
 * @param {number} startPosition the index which defines the start position
 * @param {...string} arguments substrings, which should be contained by the content string
 * @returns {object} returns an object, which contains all substrings as attributes with the respective occurrences amount
 */
export function countSubstringOccurrences( str, startPosition ) {
  let result = { content: str, count: 0, occurrences: {} };

  if( arguments.length < 3 ) {
    console.warn( 'Expected at least one substring argument' );
  }

  for( let i = 2; i < arguments.length; i++ ) {
    let count = 0;
    let positions = [];
    let position = str.indexOf( arguments[i], startPosition );
    while( position >= 0 ) {
      positions.push( position );
      count++;
      result.count++;
      position = str.indexOf( arguments[i], position + 1 );
    }

    result.occurrences[arguments[i]] = { count, positions };
  }

  return result;
}

/**
 * @method replaceAllOccurrences
 * @param {string} str the content string
 * @param {object} replacements An object which specifies the replacements. key = string to replace, value = replacing string
 * @returns {string} returns a string where all occurrences of the specified replacements are replaced
 */
export function replaceAllOccurrences( str, replacements ) {
  return Object.keys( replacements ).reduce( ( current, key ) => current.split( key ).join( replacements[key] ), str );
}

/**
 * @method includesCaseInsensitive
 * @param {string} str the content string
 * @param {string} searchString a string to be searched for within this string
 * @returns {boolean} returns true if the search string is found anywhere within the given string
 */
export function includesCaseInsensitive( str, searchString ) {
  if( str === null || str === undefined || searchString === null || searchString === undefined ) {
    console.warn( 'Expected parameters: \'str\' and \'searchString\' to be not null or undefined' );
    return false;
  }

  if( typeof str !== 'string' || typeof searchString !== 'string' ) {
    console.warn( `Expected parameters: 'str' and 'searchString' to be strings, but got ${ typeof str } and ${ typeof searchString }` );
    return false;
  }

  return str.toLowerCase().includes( searchString.toLowerCase() );
}

/**
 * @method capitalize
 * @param {string} str the content string
 * @returns {string} returns the given string as an capitalized string
 */
export function capitalize( str ) {
  if( str === null || str === undefined ) {
    console.warn( 'Expected parameters: \'str\' to be not null or undefined' );
    return false;
  }

  if( typeof str !== 'string' ) {
    console.warn( `Expected parameter: 'str' to be a string, but got ${ typeof str }` );
    return false;
  }

  return str.charAt( 0 ).toUpperCase() + str.slice( 1 );
}


String.prototype.getContentBetween = String.prototype.getContentBetween || function( ...args ) {
  return getContentBetween( this, ...args ); 
};

String.prototype.countSubstringOccurrences = String.prototype.countSubstringOccurrences || function( ...args ) {
  return countSubstringOccurrences( this, ...args ); 
};

String.prototype.replaceAllOccurrences = String.prototype.replaceAllOccurrences || function( ...args ) {
  return replaceAllOccurrences( this, ...args ); 
};

String.prototype.includesCaseInsensitive = String.prototype.includesCaseInsensitive || function( ...args ) {
  return includesCaseInsensitive( this, ...args ); 
};

String.prototype.capitalize = String.prototype.capitalize || function() {
  return capitalize( this ); 
};